import { useCallback, useMemo, useState } from 'react';
import { notificacionesApi } from '@/shared/api/services/notificacionesApi';
import { isOk, type ApiResult } from '@/shared/api/types';
import type { Notificacion } from '@/shared/domain/types';
import { useAppDispatch, useAppSelector } from '@/shared/store/hooks';
import {
  markNotificacionLocalLeida,
  markTodasLocalesLeidas,
  removeNotificacionLocal,
} from '@/shared/store/slices/notificacionesLocalSlice';
import { setQueryData, useApiQuery } from './useApi';
import { useCapacidades } from './useMercado';

export const NOTIFICACIONES_KEY = 'notificaciones';
const POR_PAGINA = 20;

interface Bandeja {
  items: Notificacion[];
  page: number;
  total: number;
  noLeidas: number;
}

export interface UseNotificaciones {
  /** Locales y de la cuenta mezcladas, las más recientes primero. */
  items: Notificacion[];
  noLeidas: number;
  isLoading: boolean;
  hayMas: boolean;
  isLoadingMore: boolean;
  loadMore: () => Promise<void>;
  leer: (n: Notificacion) => Promise<void>;
  leerTodas: () => Promise<void>;
  eliminar: (n: Notificacion) => Promise<void>;
  refetch: () => Promise<unknown>;
}

const masNuevaPrimero = (a: Notificacion, b: Notificacion): number =>
  a.creada < b.creada ? 1 : a.creada > b.creada ? -1 : 0;

/** Bandeja de notificaciones: las de alertas locales y, con sesión, las de la cuenta (paginadas). */
export function useNotificaciones(): UseNotificaciones {
  const dispatch = useAppDispatch();
  const autenticado = useAppSelector((s) => s.auth.status === 'auth');
  const locales = useAppSelector((s) => s.notificacionesLocal.items);
  const { capacidades } = useCapacidades();
  const habilitado = autenticado && capacidades.disponible;
  const [cargandoMas, setCargandoMas] = useState(false);

  const q = useApiQuery<Bandeja>(
    habilitado ? NOTIFICACIONES_KEY : null,
    async (signal): Promise<ApiResult<Bandeja>> => {
      const r = await notificacionesApi.listar({ page: 1, limit: POR_PAGINA }, { signal });
      if (!isOk(r)) return r;
      const { data, page, total, noLeidas } = r.data;
      return { ...r, data: { items: data, page, total, noLeidas } };
    },
    { staleMs: 30_000, pollMs: 120_000 },
  );

  const bandeja = q.data;
  const items = useMemo(
    () => [...locales, ...(bandeja?.items ?? [])].sort(masNuevaPrimero),
    [locales, bandeja?.items],
  );
  const noLeidasLocales = useMemo(() => locales.filter((n) => !n.leida).length, [locales]);
  const hayMas = !!bandeja && bandeja.items.length < bandeja.total;

  const loadMore = useCallback(async () => {
    if (!bandeja || cargandoMas || !hayMas) return;
    setCargandoMas(true);
    try {
      const r = await notificacionesApi.listar({ page: bandeja.page + 1, limit: POR_PAGINA });
      if (!r.ok) return;
      setQueryData<Bandeja>(NOTIFICACIONES_KEY, (previo) => {
        const base = previo ?? bandeja;
        const ids = new Set(base.items.map((n) => n.id));
        return {
          ...base,
          items: [...base.items, ...r.data.data.filter((n) => !ids.has(n.id))],
          page: r.data.page,
          total: r.data.total,
          noLeidas: r.data.noLeidas,
        };
      });
    } finally {
      setCargandoMas(false);
    }
  }, [bandeja, cargandoMas, hayMas]);

  const sobreBandeja = (cambio: (b: Bandeja) => Bandeja): void =>
    setQueryData<Bandeja>(NOTIFICACIONES_KEY, (previo) =>
      cambio(previo ?? { items: [], page: 1, total: 0, noLeidas: 0 }),
    );

  const leer = useCallback(
    async (n: Notificacion) => {
      if (n.leida) return;
      if (n.origen === 'local') {
        dispatch(markNotificacionLocalLeida(n.id));
        return;
      }
      const r = await notificacionesApi.leer(n.id);
      if (r.ok)
        sobreBandeja((b) => ({
          ...b,
          items: b.items.map((x) => (x.id === n.id ? { ...x, leida: true } : x)),
          noLeidas: Math.max(0, b.noLeidas - 1),
        }));
    },
    [dispatch],
  );

  const leerTodas = useCallback(async () => {
    dispatch(markTodasLocalesLeidas());
    if (!habilitado) return;
    const r = await notificacionesApi.leerTodas();
    if (r.ok)
      sobreBandeja((b) => ({
        ...b,
        items: b.items.map((x) => ({ ...x, leida: true })),
        noLeidas: 0,
      }));
  }, [dispatch, habilitado]);

  const eliminar = useCallback(
    async (n: Notificacion) => {
      if (n.origen === 'local') {
        dispatch(removeNotificacionLocal(n.id));
        return;
      }
      const r = await notificacionesApi.eliminar(n.id);
      if (r.ok)
        sobreBandeja((b) => ({
          ...b,
          items: b.items.filter((x) => x.id !== n.id),
          total: Math.max(0, b.total - 1),
          noLeidas: n.leida ? b.noLeidas : Math.max(0, b.noLeidas - 1),
        }));
    },
    [dispatch],
  );

  return {
    items,
    noLeidas: noLeidasLocales + (bandeja?.noLeidas ?? 0),
    isLoading: q.isLoading,
    hayMas,
    isLoadingMore: cargandoMas,
    loadMore,
    leer,
    leerTodas,
    eliminar,
    refetch: q.refetch,
  };
}
